import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, CircularProgress, Alert, Snackbar, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Container, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BookIcon from '@mui/icons-material/Book';
import HeadphonesIcon from '@mui/icons-material/Headphones';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteIcon from '@mui/icons-material/Delete';
import { booksAPI, categoriesAPI } from '../services/api';
import BookHeaderCard from '../components/books/BookHeaderCard';
import TextVersionsPanel from '../components/books/TextVersionsPanel';
import AudioVersionsPanel from '../components/books/AudioVersionsPanel';
import AudioGenerationDialog from '../components/books/AudioGenerationDialog';
import BookTranslationsPanel from '../components/books/BookTranslationsPanel';
import { useAudioPlayer } from '../context/AudioPlayerContext';
import { resolveUrl } from '../utils/urlUtils';
import SimilarBooksPanel from '../components/books/SimilarBooksPanel';

export default function BookDetail() {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [book, setBook] = useState(null);
  const [bookTexts, setBookTexts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [openAudioDialog, setOpenAudioDialog] = useState(false);
  const [selectedText, setSelectedText] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const { playTrack } = useAudioPlayer();

  useEffect(() => {
    const fetchBookAndCategories = async () => {
      try {
        setLoading(true);
        const [bookResponse, textsResponse, categoriesResponse] = await Promise.all([
          booksAPI.getBookById(bookId),
          booksAPI.getBookTexts(bookId),
          categoriesAPI.getAllCategories()
        ]);

        const bookData = bookResponse.data;
        setBook(bookData);

        setBookTexts(Array.isArray(textsResponse.data) ? textsResponse.data : []);

        const categoriesData = categoriesResponse.data?.categories || 
                               categoriesResponse.data || 
                               [];
        
        setCategories(categoriesData);
        
      } catch (error) {
        console.error('Error fetching book details:', error);
        setSnackbar({
          open: true,
          message: 'Failed to load book details',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchBookAndCategories();
  }, [bookId]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleAudioDialogOpen = (text) => {
    setSelectedText(text);
    setOpenAudioDialog(true);
  };

  const handleAudioDialogClose = () => {
    setOpenAudioDialog(false);
    setSelectedText(null);
  };

  const handleAudioGenerationSuccess = async () => {
    setSnackbar({
      open: true,
      message: 'Audio generation started successfully',
      severity: 'success'
    });

    try {
      const bookResponse = await booksAPI.getBookById(bookId);
      setBook(bookResponse.data);
    } catch (error) {
      console.error('Error refreshing book data:', error);
    }
  };

  const handleAudioGenerationError = (error) => {
    setSnackbar({
      open: true,
      message: 'Failed to generate audio',
      severity: 'error'
    });
  };

  const handlePlayAudio = (url, title) => {
    const resolvedUrl = resolveUrl(url);
    
    playTrack({
      url: resolvedUrl,
      title: title || 'Book Audio',
      artist: book?.author || 'Unknown Author'
    });
  };

  const handleBookStatusChange = (newStatus, updatedBook = null) => {
    setBook(prevBook => ({
      ...prevBook,
      status: newStatus,
      ...(updatedBook ? updatedBook : {})
    }));
  };

  const handleTextSelect = (text) => {
    handleAudioDialogOpen(text);
  };

  const handleBackToBooks = () => {
    const fromPage = searchParams.get('fromPage') || '1';
    const limit = searchParams.get('limit') || '10';
    
    navigate(`/books?page=${fromPage}&limit=${limit}`);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteConfirm = async () => {
    try {
      setDeleteLoading(true);
      
      await booksAPI.deleteBook(bookId);
      
      setSnackbar({
        open: true,
        message: 'Book deleted successfully',
        severity: 'success'
      });
      
      setDeleteDialogOpen(false);
      
      setTimeout(() => {
        handleBackToBooks();
      }, 1500);
      
    } catch (error) {
      console.error('Error deleting book:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete book: ' + (error.message || 'Unknown error'),
        severity: 'error'
      });
      setDeleteDialogOpen(false);
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading && !book) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={handleBackToBooks}
          variant="outlined" 
        >
          Back to Books
        </Button>
        
        <Button 
          startIcon={<DeleteIcon />} 
          onClick={handleDeleteClick}
          variant="outlined" 
          color="error"
        >
          Delete Book
        </Button>
      </Box>

      {book && (
        <>
          <BookHeaderCard 
            book={book} 
            categories={categories} 
            onStatusChange={handleBookStatusChange} 
            setSnackbar={setSnackbar}
          />

          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label="Text Versions" />
              <Tab label="Audio Versions" />
              <Tab label="Translations" />
              <Tab label="Similar Books" />
            </Tabs>
          </Box>
          
          {tabValue === 0 && (
            <TextVersionsPanel 
              bookId={bookId}
              bookTexts={bookTexts}
              setBookTexts={setBookTexts}
              setSnackbar={setSnackbar}
              onTextSelect={handleTextSelect}
            />
          )}
          
          {tabValue === 1 && (
            <AudioVersionsPanel 
              book={book}
              handlePlayAudio={handlePlayAudio}
            />
          )}

          {tabValue === 2 && (
            <BookTranslationsPanel
              bookId={bookId}
              setSnackbar={setSnackbar}
            />
          )}

          {tabValue === 3 && (
            <SimilarBooksPanel 
              bookId={bookId} 
              setSnackbar={setSnackbar} 
            />
          )}

          <AudioGenerationDialog
            open={openAudioDialog}
            onClose={handleAudioDialogClose}
            selectedText={selectedText}
            onSuccess={handleAudioGenerationSuccess}
            onError={handleAudioGenerationError}
          />
        </>
      )}

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteClose}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Book
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete "{book?.title}"? This action cannot be undone.
            All associated text versions, audio files, and relationships will also be deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={deleteLoading}
            startIcon={deleteLoading && <CircularProgress size={16} color="inherit" />}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
